import { useState, useEffect, useCallback } from 'react'
import { fetchDraftOrders, parseMetafield, type DraftOrderListItem, type PaymentMetafield, type PageInfo } from '../lib/graphql'
import { apiPost } from '../lib/api'

type LinkedOrder = { orderId: string | null; orderName: string | null }

// Drafts completed outside our app (e.g. from BCM) have the order link in our DB,
// not on the draft's metafield — so fill in orderId/orderName from the backend.
async function fillLinkedOrders(rows: DraftOrderRow[]): Promise<void> {
    const paymentIds = rows
        .filter((r) => r.payment?.paymentId && !r.payment.orderId)
        .map((r) => r.payment!.paymentId)
    if (!paymentIds.length) return
    const map = await apiPost<Record<string, LinkedOrder>>('/api/payments/orders', { paymentIds })
    for (const r of rows) {
        const linked = r.payment?.paymentId ? map[r.payment.paymentId] : null
        if (linked?.orderId && r.payment) {
            r.payment = { ...r.payment, orderId: linked.orderId, orderName: linked.orderName || undefined }
        }
    }
}

export type DraftOrderRow = DraftOrderListItem & {
    payment: PaymentMetafield | null
}

const PAGE_SIZE = 25

export function useDraftOrders() {
    const [items, setItems] = useState<DraftOrderRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [pageInfo, setPageInfo] = useState<PageInfo>({
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null,
    })

    const load = useCallback(async (params: { first?: number; after?: string; last?: number; before?: string } = { first: PAGE_SIZE }) => {
        setLoading(true)
        setError(null)
        try {
            const result = await fetchDraftOrders(params)
            const rows: DraftOrderRow[] = result.items.map(item => ({
                ...item,
                payment: parseMetafield(item.metafield),
            }))
            // Best-effort: fill in order links for drafts completed outside our app.
            try { await fillLinkedOrders(rows) } catch { /* list still works without links */ }
            setItems(rows)
            setPageInfo(result.pageInfo)
        } catch (err: any) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { load() }, [load])

    // Auto-refresh when page regains focus
    useEffect(() => {
        const handleFocus = () => load()
        window.addEventListener('focus', handleFocus)
        return () => window.removeEventListener('focus', handleFocus)
    }, [load])

    const nextPage = useCallback(() => {
        if (pageInfo.hasNextPage && pageInfo.endCursor) {
            load({ first: PAGE_SIZE, after: pageInfo.endCursor })
        }
    }, [pageInfo, load])

    const prevPage = useCallback(() => {
        if (pageInfo.hasPreviousPage && pageInfo.startCursor) {
            load({ last: PAGE_SIZE, before: pageInfo.startCursor })
        }
    }, [pageInfo, load])

    const refresh = useCallback(() => {
        load()
    }, [load])

    return {
        items,
        loading,
        error,
        hasNextPage: pageInfo.hasNextPage,
        hasPreviousPage: pageInfo.hasPreviousPage,
        nextPage,
        prevPage,
        refresh,
    }
}
